import { logger } from './logger';
import api from './api';

// Configuration constants for image conversion
const HEIC_CONVERSION_TIMEOUT_MS = 10000;
const MAX_PROFILE_IMAGE_SIZE_PX = 2000;

// Must match backend ALLOWED_PROFILE_CONTENT_TYPES in routers/profile.py.
// HEIC/HEIF are converted to JPEG client-side first; if that conversion fails
// (e.g. Chrome/Firefox have no native HEIC decoder) the original HEIC is left
// as-is, which the backend rejects with 400. We surface a clear message here
// instead of letting that become an opaque "upload failed".
const ALLOWED_PROFILE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Convert an image file to JPEG format using Canvas API.
 * This handles HEIC/HEIF files on browsers that support them natively (Safari, iOS).
 * For browsers without native HEIC support, the original file is returned.
 */
const convertToJpeg = async (file: File): Promise<File> => {
  // If already JPEG/PNG, no conversion needed
  if (file.type === 'image/jpeg' || file.type === 'image/png') {
    return file;
  }

  // If file type is empty or not a known image format that needs conversion, skip
  // This avoids unnecessary conversion attempts for common image formats
  const needsConversion = file.type.includes('heic') || file.type.includes('heif');
  if (!needsConversion) {
    return file;
  }

  // In test environment or if URL.createObjectURL is not available, skip conversion
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set a timeout to handle cases where image loading hangs
    const timeout = setTimeout(() => {
      if (img.src) URL.revokeObjectURL(img.src);
      resolve(file);
    }, HEIC_CONVERSION_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(img.src);
      // Set canvas size to image dimensions
      const maxSize = MAX_PROFILE_IMAGE_SIZE_PX;
      let width = img.width;
      let height = img.height;

      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        resolve(file);
        return;
      }

      // Draw and convert to JPEG
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const convertedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(convertedFile);
          } else {
            resolve(file);
          }
        },
        'image/jpeg',
        0.9 // Quality
      );
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(img.src);
      // Browser doesn't support this image format, return original
      resolve(file);
    };

    // Create object URL to load the image
    try {
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      logger.debug('Failed to create object URL for profile image', error);
      clearTimeout(timeout);
      resolve(file);
    }
  });
};

/**
 * Build the owner/catalog query params shared by every CV-asset request.
 * Returns a fresh object (never mutates its inputs).
 *
 * `catalogAliId` scopes the asset to one of the user's OWNED catalogs
 * (primary keeps the legacy key, add-ons get a per-catalog key). It is only
 * sent for own-catalog requests; delegated requests use `ownerId` instead.
 */
const assetParams = (
  ownerId?: string,
  catalogAliId?: string
): Record<string, string> => ({
  ...(ownerId ? { owner_id: ownerId } : {}),
  ...(catalogAliId ? { catalogAliId } : {}),
});

/**
 * Get a presigned URL for uploading a profile image directly to S3.
 * This bypasses the API server for the actual upload, avoiding WAF body size limits.
 */
const getUploadUrl = async (
  contentType: string,
  ownerId?: string,
  catalogAliId?: string
): Promise<{ uploadUrl: string; bucket: string; key: string }> => {
  // Param name MUST be `content_type` (snake_case) to match the FastAPI
  // endpoint's query parameter. The backend signs the presigned PUT URL for
  // this content type; if it doesn't bind, the URL is signed for the default
  // `image/jpeg` and any non-JPEG upload fails S3's signature check (403
  // SignatureDoesNotMatch). Mirrors the logo/PDF utilities in cvAssetsApi.ts.
  const response = await api.get('/api/profile/image/upload-url', {
    params: { content_type: contentType, ...assetParams(ownerId, catalogAliId) }
  });
  return response.data;
};

/**
 * Upload a profile image using presigned URL (direct-to-S3 upload).
 * This is more efficient than base64 upload and bypasses WAF body size limits.
 * HEIC/HEIF images are automatically converted to JPEG before upload.
 */
export const uploadProfileImage = async (
  file: File,
  ownerId?: string,
  catalogAliId?: string
): Promise<{ imageUrl: string }> => {
  try {
    // Convert HEIC/HEIF or other formats to JPEG for compatibility
    const convertedFile = await convertToJpeg(file);

    // Guard: if conversion left a type the backend won't accept (e.g. HEIC that
    // couldn't be decoded in this browser), reject with a clear, actionable
    // message rather than letting the backend 400 surface as a generic failure.
    const uploadType = convertedFile.type || 'image/jpeg';
    if (!ALLOWED_PROFILE_CONTENT_TYPES.includes(uploadType)) {
      throw new Error(
        'This image format is not supported. Please upload a JPEG, PNG, or WebP image. ' +
          '(HEIC photos could not be converted in this browser — try Safari, or convert to JPEG first.)'
      );
    }

    // Step 1: Get presigned URL from our API. Pass the file's ACTUAL content
    // type (HEIC/HEIF are converted to JPEG above; PNG/WebP/etc. pass through
    // unchanged). The backend signs the presigned PUT URL for this exact type,
    // and the PUT below MUST send the same type — do not hardcode 'image/jpeg'
    // here, or non-JPEG uploads fail S3's signature check.
    const { uploadUrl } = await getUploadUrl(convertedFile.type || 'image/jpeg', ownerId, catalogAliId);

    // Step 2: Upload directly to S3 using presigned URL
    // Note: x-amz-server-side-encryption header is already signed into the presigned URL
    // by the backend, so we must NOT include it again here or S3 will reject with
    // SignatureDoesNotMatch error
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: convertedFile,
      headers: {
        'Content-Type': convertedFile.type || 'image/jpeg',
      },
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      logger.error('[Profile Image] S3 upload failed', {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
        error: errorText
      });
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    // Step 3: Get the image URL for display
    const imageData = await getProfileImageUrl(ownerId, catalogAliId);
    if (!imageData) {
      throw new Error('Failed to get profile image URL after upload');
    }

    return imageData;
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number; statusText?: string; data?: unknown; headers?: unknown } };
    logger.error('[Profile Image] Upload failed', {
      message: axiosError.message,
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data,
      headers: axiosError.response?.headers
    });
    throw error;
  }
};

export const getProfileImageUrl = async (
  ownerId?: string,
  catalogAliId?: string
): Promise<{ imageUrl: string } | null> => {
  try {
    const params = assetParams(ownerId, catalogAliId);
    const response = await api.get(
      '/api/profile/image',
      Object.keys(params).length ? { params } : undefined
    );
    return response.data;
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number; data?: unknown } };
    const httpStatus = axiosError.response?.status;
    // 403 → delegation rejected (e.g. expired mid-session): treat as "no asset"
    // so callers clear stale state instead of leaving a prior owner's image visible.
    if (httpStatus === 404 || httpStatus === 403) {
      if (httpStatus === 403) {
        logger.info('[Profile Image] Delegation rejected; returning null', {
          ownerId,
          status: httpStatus,
        });
      }
      return null;
    }
    logger.error('[Profile Image] Failed to fetch URL', {
      message: axiosError.message,
      status: httpStatus,
      data: axiosError.response?.data
    });
    throw error;
  }
};

export const deleteProfileImage = async (
  ownerId?: string,
  catalogAliId?: string
): Promise<void> => {
  try {
    const params = assetParams(ownerId, catalogAliId);
    await api.delete('/api/profile/image', Object.keys(params).length ? { params } : undefined);
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number; data?: unknown } };
    logger.error('[Profile Image] Delete failed', {
      message: axiosError.message,
      status: axiosError.response?.status,
      data: axiosError.response?.data
    });
    throw error;
  }
};
